import { DatePicker } from 'antd';
import '../VtxDatePicker.scss';
import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import { RangePickerProps } from 'antd/lib/date-picker';
import { LocalDate } from '@js-joda/core';
import moment from 'moment';
import { IAutomatable, preventDataAutomationOverriding } from '../../../helpers/IAutomatable';

export interface IRange<T> {
    startDay: T;
    endDay: T;
}

export interface VtxDayRangePickerProps
    extends Omit<
            RangePickerProps,
            | 'picker'
            | 'showTime'
            | 'onChange'
            | 'dateRender'
            | 'monthCellRender'
            | 'onOk'
            | 'onPanelChange'
            | 'disabledDate'
            | 'value'
            | 'onSelect'
            | 'format'
            | 'defaultValue'
            | 'defaultPickerValue'
            | 'format'
        >,
        IAutomatable {
    /**
     * to set default date, if start time or end time is null or undefined, the date range will be an open interval
     */
    defaultValue?: Readonly<IRange<LocalDate>>;
    /**
     * to set default picker date
     */
    defaultPickerValue?: Readonly<IRange<LocalDate>>;
    onChange?: (days: IRange<LocalDate | null>) => void;
    disabledDate?: (day: LocalDate) => boolean;
    onOk?: (days: IRange<LocalDate | null>) => void;
    /**
     * The way the date will be shown once selected. Uses the MomentJS format string patterns. Read more at: https://momentjs.com/docs/#/parsing/string-format/
     * @default MM/DD/YYYY
     */
    format?: string;
    /**
     * @deprecated if you want custom styling, please reach out to the UX team for approval
     */
    dateRender?: never;
    /**
     * @deprecated Use one of the named widgets instead like VtxDayPicker, VtxRangePicker, VtxYearPicker instead
     */
    picker?: never;
    /**
     * Tells the widget whether or not to show the time portion of widget. If you don't want to show this then use VtxDayPicker instead.
     */
    showTime?: never;
    value?: IRange<LocalDate | null>;
    /**
     * @deprecated Currently panel types must remain static
     */
    onPanelChange?: never;
}

export const VtxDayRangePicker: FunctionComponent<VtxDayRangePickerProps> = React.memo(
    (props: VtxDayRangePickerProps) => {
        const automationClass = 'vtx-day-range-picker';
        const {
            className,
            onChange,
            dateRender,
            onOk,
            disabledDate,
            value,
            defaultValue,
            defaultPickerValue,
            format,
            ...otherProps
        } = preventDataAutomationOverriding(props);

        const formatOrDefault = format ? format : 'MM/DD/YYYY';
        const isoDayPattern = 'YYYY-MM-DD';

        const wrappedOnChange: RangePickerProps['onChange'] = date => {
            if (onChange && date) {
                const startDay = date[0] ? LocalDate.parse(date[0].format(isoDayPattern)) : null;
                const endDay = date[1] ? LocalDate.parse(date[1].format(isoDayPattern)) : null;
                onChange({ startDay: startDay, endDay: endDay });
            } else if (onChange) {
                onChange({ startDay: null, endDay: null });
            }
        };

        const wrappedOnOk: RangePickerProps['onOk'] = date => {
            if (onOk && date) {
                const startDay = date[0] ? LocalDate.parse(date[0].format(isoDayPattern)) : null;
                const endDay = date[1] ? LocalDate.parse(date[1].format(isoDayPattern)) : null;
                onOk({ startDay: startDay, endDay: endDay });
            }
        };

        const wrappedDisabledDate: RangePickerProps['disabledDate'] = disabledDate
            ? date => {
                  const localDate = LocalDate.parse(date.format(isoDayPattern));
                  return disabledDate(localDate);
              }
            : undefined;

        const classNamesCombined = classNames(automationClass, className);

        const valueEndDateToMoment = value?.endDay ? moment(value.endDay.toString()) : null;
        const valueStartDateToMoment = value?.startDay ? moment(value.startDay.toString()) : null;
        return (
            <div data-automation-class={automationClass} className={classNamesCombined}>
                <DatePicker.RangePicker
                    picker="date"
                    showTime={false}
                    disabledDate={wrappedDisabledDate}
                    defaultValue={
                        defaultValue
                            ? [
                                  moment(defaultValue?.startDay.toString()),
                                  moment(defaultValue?.endDay.toString()),
                              ]
                            : undefined
                    }
                    defaultPickerValue={
                        defaultPickerValue
                            ? [
                                  moment(defaultPickerValue?.startDay.toString()),
                                  moment(defaultPickerValue?.endDay.toString()),
                              ]
                            : undefined
                    }
                    value={value ? [valueStartDateToMoment, valueEndDateToMoment] : undefined}
                    onChange={wrappedOnChange}
                    onOk={wrappedOnOk}
                    format={formatOrDefault}
                    {...otherProps}
                />
            </div>
        );
    }
);
